<template>
  <div class="card">
    <!-- Clickable Header with Chevron -->
    <div 
      @click="toggleExpanded" 
      class="flex items-center justify-between mb-3 cursor-pointer select-none hover:opacity-80 transition-opacity"
      role="button"
      :aria-expanded="isExpanded"
      :aria-label="isExpanded ? 'Collapse console log' : 'Expand console log'"
    >
      <div class="flex items-center gap-2">
        <h2 class="card-header mb-0">Console Log</h2>
        <svg 
          xmlns="http://www.w3.org/2000/svg" 
          fill="none" 
          viewBox="0 0 24 24" 
          stroke-width="2" 
          stroke="currentColor" 
          class="w-5 h-5 text-gray-400 transition-transform duration-200"
          :class="{ 'rotate-180': isExpanded }"
        >
          <path stroke-linecap="round" stroke-linejoin="round" d="m19.5 8.25-7.5 7.5-7.5-7.5" />
        </svg>
      </div>
      <div class="flex gap-2">
        <button 
          @click.stop="clearLog" 
          class="btn-secondary text-sm py-1"
          :disabled="consoleLog.length === 0"
        >
          Clear
        </button>
      </div>
    </div>
    
    <div 
      v-show="isExpanded"
      class="bg-gray-900 text-gray-100 p-4 rounded font-mono text-sm overflow-y-auto transition-all"
      :class="isExpanded ? 'h-96' : 'h-0'"
      ref="logContainer"
      role="log"
      aria-live="polite"
      aria-atomic="false"
    >
      <div 
        v-if="consoleLog.length === 0" 
        class="text-gray-500"
      >
        No log entries. Waiting for activity...
      </div>
      <div
        v-for="(entry, index) in displayLog"
        :key="`log-${index}-${entry.timestamp}`"
        class="log-entry"
        :class="`log-${entry.type}`"
      >
        <span class="log-timestamp">[{{ formatTime(entry.timestamp) }}]</span>
        <span class="log-message">{{ entry.message }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useDeviceStore } from '@/stores/device';

const deviceStore = useDeviceStore();
const logContainer = ref(null);
const isExpanded = ref(false); // Changed to collapsed by default

// Get console log from store (reversed so oldest is at top, newest at bottom)
const consoleLog = computed(() => deviceStore.consoleLog);
const displayLog = computed(() => [...consoleLog.value].reverse());

/**
 * Format timestamp to HH:MM:SS
 */
function formatTime(timestamp) {
  const date = new Date(timestamp);
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  const seconds = String(date.getSeconds()).padStart(2, '0');
  return `${hours}:${minutes}:${seconds}`;
}

/**
 * Clear all log entries
 */
function clearLog() {
  deviceStore.clearLog();
}

/**
 * Toggle expanded/collapsed state
 */
function toggleExpanded() {
  isExpanded.value = !isExpanded.value;
}

/**
 * Auto-scroll to bottom when new entries are added
 */
watch(
  () => displayLog.value.length,
  async () => {
    if (isExpanded.value && logContainer.value) {
      await nextTick();
      logContainer.value.scrollTop = logContainer.value.scrollHeight;
    }
  }
);
</script>

<style scoped>
.log-entry {
  margin-bottom: 0.25rem;
  line-height: 1.5;
  word-break: break-word;
}

.log-timestamp {
  color: #9ca3af;
  margin-right: 0.5rem;
  font-weight: 600;
}

.log-message {
  color: #e5e7eb;
}

/* Color coding by log type */
.log-info .log-message {
  color: #93c5fd; /* blue-300 */
}

.log-success .log-message {
  color: #86efac; /* green-300 */
}

.log-warning .log-message {
  color: #fcd34d; /* yellow-300 */
}

.log-error .log-message {
  color: #fca5a5; /* red-300 */
  font-weight: 600;
}

/* Enhanced contrast for outdoor visibility */
.log-info .log-timestamp,
.log-success .log-timestamp,
.log-warning .log-timestamp,
.log-error .log-timestamp {
  color: #d1d5db; /* gray-300 */
}

/* Smooth transitions */
.transition-all {
  transition: height 0.3s ease-in-out;
}

/* Ensure good scrollbar visibility on dark background */
.bg-gray-900::-webkit-scrollbar {
  width: 10px;
}

.bg-gray-900::-webkit-scrollbar-track {
  background: #1f2937; /* gray-800 */
  border-radius: 4px;
}

.bg-gray-900::-webkit-scrollbar-thumb {
  background: #4b5563; /* gray-600 */
  border-radius: 4px;
}

.bg-gray-900::-webkit-scrollbar-thumb:hover {
  background: #6b7280; /* gray-500 */
}
</style>
